import {memo, useRef, useEffect, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useProjectFilter} from '../../hooks/useProjectFilter.js';
import ServerSidePaginationAggrid from '../../../common/ServerSidePaginationAggrid.jsx';
import AgGridReact from '../../../common/AgGrid/AgGridReact.jsx';
import {gridThemeNoBorder} from '../../../common/AgGrid/gridTheme.js';
import TooltipWithRef from '../../../common/Tooltip.jsx';
import Turbolink from '../../../common/Turbolink.jsx';
import {Adalicon} from '../../../common/Icons.jsx';
import {valueGetter, DATE_FILTER_COMPARATOR} from '../../../workflow_inbox/ManageWorkflowInbox.jsx';

import {
  workflow_inbox_list_path,
  monitoring_report_project_reports_path,
  project_report_edit_report_path,
  project_report_review_path,
  project_report_decision_path,
  workflow_inbox_mark_as_read_path,
  project_lists_for_add_path,
  project_list_add_project_to_list_path,
} from '~/routes';

const PAGINATION_PAGE_SIZE = 10;

const ButtonsCellRenderer = memo((params) => {
  const {project_report_id, project_id, phase_id, current_status} = params.getValue();

  return (<>
    {current_status === 'pending_approval' ?
      <Turbolink
        href={project_report_decision_path({project_report_id, project_id, report_phase_id: phase_id})}
        className='btn btn-navy btn-xs'
      >
        <Adalicon type='gavel'/> {I18n.t('decision')}
      </Turbolink>
      : current_status === 'pending_review' &&
      <Turbolink
        href={project_report_review_path({project_report_id, project_id, report_phase_id: phase_id})}
        className='btn btn-navy btn-xs'
      >
        <Adalicon type='search'/> {I18n.t('review')}
      </Turbolink>
    }
  </>)
});

const WorkflowInboxWidget = ({widgetId, permissions = {}}) => {
  const gridRef = useRef(null);
  const gridAdvancedFilterRef = useRef(null);
  const projectListsRef = useRef([]);
  const gridId = `workflow-inbox-grid-widget-${widgetId}`;

  const projectFilterEnabled = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.projectFilterEnabled);
  const advancedFilterModel = useSelector((state) => state.dashboard.widgets[widgetId].settings?.advancedFilterModel);
  const {activeProjectIds, isFiltered} = useProjectFilter();
  const effectiveProjectIds = projectFilterEnabled && isFiltered ? activeProjectIds.map(p => p.id) : [];

  const COLUMN_DEFS = [
    {
      headerName: '',
      headerValueGetter: (params) => {
        if (params.location === 'advancedFilter') return I18n.t('read');
        return '';
      },
      colId: 'is_read',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        optionMap: new Map([true, false].map(v => [v, I18n.t(v ? 'read' : 'unread')])),
        valueFormatter: ({value}) => value ? I18n.t('read') : I18n.t('unread'),
      },
      valueGetter: (params) => params.data[params.colDef.colId],
      cellRenderer: ({value, data: {project_report_id}}) => {
        if (value) return;
        return (
          <TooltipWithRef
            trigger='hover'
            title={I18n.t('mark_as_read')}
            style={{maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
          >
            <span onClick={(e) => handleMarkAsRead(e, project_report_id)} style={{cursor: 'pointer'}}>
              <i className="fa fa-circle text-primary"/>
            </span>
          </TooltipWithRef>
        )
      },
      width: 50,
      minWidth: 50,
      suppressHeaderMenuButton: true,
      cellStyle: {
        textAlign: 'center',
        padding: 2,
      }
    },
    {
      field: I18n.t('workflow_name'),
      colId: 'workflow_name',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: (params) => {
        const {monitoring_report_id, workflow_name} = (params.data || {});
        return (
          <a href={monitoring_report_project_reports_path({id: monitoring_report_id})}>{workflow_name}</a>
        )
      }
    },
    {
      field: I18n.t('type'),
      colId: 'report_type',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: ({value}) => {
        return I18n.t(`report_type_${value}`);
      },
    },
    {
      field: I18n.t('phase'),
      colId: 'phase_name',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      field: I18n.t('status'),
      colId: 'current_status',
      editable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['pending_review', 'pending_approval'],
        optionMap: new Map(['pending_review', 'pending_approval'].map(v => [v, I18n.t(`project_report_current_status_${v}`)])),
        valueFormatter: ({value}) => I18n.t(`project_report_current_status_${value}`),
      },
      valueGetter: valueGetter,
      cellRenderer: ({value}) => I18n.t(`project_report_current_status_${value}`)
    },
    {
      field: I18n.t('assigned_at'),
      colId: 'assigned_at',
      editable: false,
      filter: 'agDateColumnFilter',
      valueGetter: valueGetter,
      valueFormatter: ({data: {assigned_at}}) => DT.dt(assigned_at, {format: null, datetime: true, timezone: false}),
      filterParams: {
        ...DATE_FILTER_COMPARATOR
      },
    },
    {
      field: I18n.t('project_report_identifier'),
      colId: 'project_report_identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
      cellRenderer: (params) => {
        const {monitoring_report_id, project_report_id, project_id, project_report_identifier} = (params.data || {});
        return (
          <a href={project_report_edit_report_path({
            project_id,
            project_report_id,
            report_id: monitoring_report_id
          })}>{project_report_identifier}</a>
        )
      }
    },
    {
      field: I18n.t('project_identifier'),
      colId: 'project_identifier',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      field: I18n.t('project_title_long'),
      colId: 'project_title',
      editable: false,
      filter: 'agTextColumnFilter',
      valueGetter: valueGetter,
    },
    {
      headerName: '',
      colId: 'action_buttons',
      editable: false,
      filter: false,
      sortable: false,
      width: 105,
      minWidth: 105,
      suppressHeaderMenuButton: true,
      cellRenderer: ButtonsCellRenderer,
      valueGetter: (params) => {
        params.data ||= {};
        return {...params.data};
      },
      pinned: 'right',
    },
    {
      field: '-',
      colId: 'global_search',
      editable: false,
      hide: true,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      suppressColumnsToolPanel: true
    }
  ];

  const handleGridReady = useCallback((gridApi) => {
    gridApi.sizeColumnsToFit();
  }, []);

  const handleAdvancedFilterGridReady = ({api}) => {
    gridAdvancedFilterRef.current.api = api;
    if (advancedFilterModel) {
      api.setAdvancedFilterModel(advancedFilterModel);
    }
  };

  const addProjectToList = (selectedListId, projectId) => {
    jQuery.post(project_list_add_project_to_list_path(), {add_to_list: {project_list_id: selectedListId, project_id: projectId}})
      .done((_res) => {
        window.ToastNotification({message: I18n.t('project_added_to_list_successfully'), horizontal: 'center', vertical: 'top', autoHideDuration: 5});
      })
  }

  // Load personal project lists only when the user has permission
  useEffect(() => {
    if (permissions?.add_to_project_list) {
      fetch(project_lists_for_add_path())
        .then(res => res.json())
        .then(res => projectListsRef.current = res);
    }
  }, []);

  useEffect(() => {
    let resizeTimeout;

    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (gridRef.current?.api) {
          gridRef.current.api.sizeColumnsToFit();
        }
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  const handleMarkAsRead = (e, project_report_id) => {
    const el = e.currentTarget;
    el.innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;

    jQuery.post(workflow_inbox_mark_as_read_path({project_report_id}), {}, () => {
      el.innerHTML = '';
      jQuery(".tooltip.fade.top.in").remove();
    })
    .fail(() => {
      el.innerHTML = `<i class="fa fa-circle text-primary"></i>`;
    });
  };

  const getContextMenuItems = useCallback((params) => {
    const defaultItems = ['copy', 'copyWithHeaders', 'paste', 'separator', 'export'];
    const additionalItems = [];

    // Display personal project lists only when the user has permission and there are lists available
    if (permissions?.add_to_project_list && projectListsRef.current.length) {
      additionalItems.push(
        'separator',
        {
          name: I18n.t('add_to_list'),
          icon: '<i class="fa fa-plus"></i>',
          subMenu: projectListsRef.current.map(list => ({name: list.name, action: () => addProjectToList(list.id, params.node.data.project_id)}))
        }
      );
    }
    return [...defaultItems, ...additionalItems];
  }, []);

  return (
    <div style={{height: '100%'}}>
      <ServerSidePaginationAggrid
        externalGridRef={gridRef}
        onGridReadyCallback={handleGridReady}
        fetchPath={workflow_inbox_list_path({})}
        fetchBody={{
          ...(effectiveProjectIds.length ? {project_ids: effectiveProjectIds} : {}),
          ...(advancedFilterModel && {advancedFilterModel})
        }}
        refreshBodyChanges={true}
        columnDefs={COLUMN_DEFS}
        defaultGridOptions={{
          pagination: true,
          paginationAutoPageSize: true,
          cacheBlockSize: PAGINATION_PAGE_SIZE,
          domLayout: 'normal',
          getRowStyle: ({data}) => {
            if (data?.is_read) return null;
            return {background: 'var(--ag-row-hover-color)'};
          },
          getContextMenuItems
        }}
        style={{
          height: 'calc(100% - 38px)'
        }}
      />
      <div id={gridId}>
        <AgGridReact
          theme={gridThemeNoBorder}
          ref={gridAdvancedFilterRef}
          columnDefs={COLUMN_DEFS}
          onGridReady={handleAdvancedFilterGridReady}
          rowModelType={'serverSide'}
          enableAdvancedFilter={true}
        />
      </div>
    </div>
  )
}

export default WorkflowInboxWidget;
